import { Injectable } from '@angular/core';
import posthog from 'posthog-js';

@Injectable({
  providedIn: 'root'
})
export class PostHogService {

  constructor() { }

  /**
   * Identify a user with PostHog
   * @param distinctId - Unique identifier for the user
   * @param properties - User properties to set
   */
  identify(distinctId: string, properties?: Record<string, any>): void {
    if (this.isPostHogAvailable()) {
      posthog.identify(distinctId, properties);
    }
  }

  /**
   * Set user properties
   * @param properties - Properties to set for the current user
   */
  setPersonProperties(properties: Record<string, any>): void {
    if (this.isPostHogAvailable()) {
      posthog.setPersonProperties(properties);
    }
  }

  /**
   * Set a single user property
   * @param key - Property key
   * @param value - Property value
   */
  setPersonProperty(key: string, value: any): void {
    if (this.isPostHogAvailable()) {
      posthog.setPersonProperties({ [key]: value });
    }
  }

  /**
   * Get the current user's distinct ID
   * @returns The current user's distinct ID or null if not available
   */
  getDistinctId(): string | null {
    if (this.isPostHogAvailable()) {
      return posthog.get_distinct_id();
    }
    return null;
  }

  /**
   * Get the current user's properties
   * @returns The current user's properties or null if not available
   */
  getPersonProperties(): Record<string, any> | null {
    if (this.isPostHogAvailable()) {
      // Note: getPersonProperties is not available in posthog-js
      // This method returns null as the property retrieval is not supported
      return null;
    }
    return null;
  }

  /**
   * Track an event
   * @param event - Event name
   * @param properties - Event properties
   */
  track(event: string, properties?: Record<string, any>): void {
    if (this.isPostHogAvailable()) {
      posthog.capture(event, properties);
    }
  }

  /**
   * Reset the current user (logout)
   */
  reset(): void {
    if (this.isPostHogAvailable()) {
      posthog.reset();
    }
  }

  /**
   * Check if PostHog is available and initialized
   * @returns True if PostHog is available
   */
  private isPostHogAvailable(): boolean {
    return typeof posthog !== 'undefined' && posthog.isFeatureEnabled !== undefined;
  }

  /**
   * Get PostHog instance for advanced usage
   * @returns PostHog instance or null if not available
   */
  getInstance(): typeof posthog | null {
    return this.isPostHogAvailable() ? posthog : null;
  }
}
